/**
 * What this deployment is: the sign-in endpoint, the application id, the API
 * it talks to.
 *
 * Vite bakes `import.meta.env` into the bundle, which makes a built image
 * specific to the environment it was built for. That is the wrong shape for an
 * image that is published once and deployed by somebody else on a host its
 * builder has never seen: "deploy this container" would mean "rebuild this
 * container with your own values first".
 *
 * So the server serves `/config.json` from its own environment and this reads
 * it before anything else starts. The values baked at build time stay as the
 * fallback, which is what keeps `pnpm dev` working with a `.env` and nothing
 * else — and what makes a deployment that forgets to set them behave exactly
 * as it did before rather than mysteriously worse.
 */

export interface RuntimeConfig {
  logtoEndpoint: string;
  logtoAppId: string;
  apiAudience: string;
  apiBaseUrl: string;
  /** `Label=address|…`, and empty anywhere that is not a demo. */
  demoAccounts: string;
}

const baked: RuntimeConfig = {
  logtoEndpoint: (import.meta.env.VITE_LOGTO_ENDPOINT as string) ?? '',
  logtoAppId: (import.meta.env.VITE_LOGTO_APP_ID as string) ?? '',
  apiAudience: (import.meta.env.VITE_API_AUDIENCE as string) ?? '',
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL as string) ?? '/api',
  demoAccounts: (import.meta.env.VITE_DEMO_ACCOUNTS as string) ?? '',
};

let current: RuntimeConfig = baked;

/**
 * Asked for once, before the app is created.
 *
 * A deployment that does not serve the file — `pnpm dev`, where Vite serves
 * the app itself — falls back to what was baked, so this never has to succeed
 * for the app to work. It is given a short deadline for the same reason: a
 * server that accepts the connection and never answers must not be able to
 * leave somebody on a blank page.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const res = await fetch('/config.json', {
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return current;
    const served = await res.json() as Partial<RuntimeConfig>;
    // Only what it actually said: an empty string in the file is a value the
    // deployment did not set, not an instruction to blank the fallback.
    current = {
      logtoEndpoint: served.logtoEndpoint || baked.logtoEndpoint,
      logtoAppId: served.logtoAppId || baked.logtoAppId,
      apiAudience: served.apiAudience || baked.apiAudience,
      apiBaseUrl: served.apiBaseUrl || baked.apiBaseUrl,
      demoAccounts: served.demoAccounts || baked.demoAccounts,
    };
  } catch {
    // Offline, blocked, or too slow. The baked values are already here.
  }
  return current;
}

export function runtimeConfig(): RuntimeConfig {
  return current;
}
